import { html } from 'lit';
import { brandAssetRepository } from '@vivaporta/brand';
import type { LandingContent } from '../../shared/content/landingContent.js';
import { editorSemanticText } from '../editor/editorSemanticText.js';
import { socialIcon, socialPlatformForHref } from '../components/socialIcons.js';
import {
  editorNodeClass,
  editorNodeClick,
  editorNodePointerDown,
  editorNodeStyle,
  type LandingEditorRenderOptions,
} from '../editor/landingEditorRender.js';

const navHeading = (locale: LandingContent['locale']) => {
  if (locale === 'tr') return 'Menü';
  if (locale === 'en') return 'Navigation';
  return 'Navigation';
};

const socialHeading = (locale: LandingContent['locale']) => {
  if (locale === 'tr') return 'Bizi takip edin';
  if (locale === 'en') return 'Follow us';
  return 'Folgen Sie uns';
};

const editorLoginLabel = (locale: LandingContent['locale']) => {
  if (locale === 'tr') return 'Editör girişi';
  if (locale === 'en') return 'Editor login';
  return 'Editor Login';
};

const footerNavigationLabel = (locale: LandingContent['locale']) => {
  if (locale === 'tr') return 'Alt menü';
  if (locale === 'en') return 'Footer navigation';
  return 'Footer-Navigation';
};

const socialNavigationLabel = (locale: LandingContent['locale']) => {
  if (locale === 'tr') return 'Sosyal medya';
  return 'Social Media';
};

export const footerSection = (content: LandingContent, editor?: LandingEditorRenderOptions) => html`
  <footer
    class=${editorNodeClass(editor, 'footer.container', 'vp-footer')}
    style=${editorNodeStyle(editor, 'footer.container')}
    @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'footer.container', event)}
    @click=${(event: MouseEvent) => editorNodeClick(editor, 'footer.container', event)}
  >
    <div class="vp-shell vp-footer-grid">
      <div class="vp-footer-brand">
        <img
          class=${editorNodeClass(editor, 'footer.logo')}
          style=${editorNodeStyle(editor, 'footer.logo')}
          src=${brandAssetRepository.navbarLogoDataUri}
          alt="Viva Porta"
          @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, 'footer.logo', event)}
          @click=${(event: MouseEvent) => editorNodeClick(editor, 'footer.logo', event)}
        />
        ${editorSemanticText({ editor, elementId: 'footer.text', fallbackTag: 'p', content: content.footer.text })}
      </div>

      <div class="vp-footer-col vp-footer-col-nav">
        <h3 class="vp-footer-heading">${navHeading(content.locale)}</h3>
        <nav class="vp-footer-nav" aria-label=${footerNavigationLabel(content.locale)}>
          ${content.footer.links.map(
            (link, index) => html`
              <div class=${editorNodeClass(editor, `footer.link.${index}`, 'vp-footer-link')}
                style=${editorNodeStyle(editor, `footer.link.${index}`)}
                @pointerdown=${(event: PointerEvent) => editorNodePointerDown(editor, `footer.link.${index}`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `footer.link.${index}`, event)}>
              <a
                class=${editorNodeClass(editor, `footer.link.${index}.label`)}
                style=${editorNodeStyle(editor, `footer.link.${index}.label`)}
                href=${link.href}
                @pointerdown=${(event: PointerEvent) =>
                  editorNodePointerDown(editor, `footer.link.${index}.label`, event)}
                @click=${(event: MouseEvent) => editorNodeClick(editor, `footer.link.${index}.label`, event)}
                >${link.label}</a
              >
              </div>
            `,
          )}
        </nav>
      </div>

      ${content.footer.socialLinks?.length
        ? html`
            <div class="vp-footer-col vp-footer-col-social">
              <h3 class="vp-footer-heading">${socialHeading(content.locale)}</h3>
              <nav class="vp-footer-social" aria-label=${socialNavigationLabel(content.locale)}>
                ${content.footer.socialLinks.map(
                  (link, index) => html`
                    <a
                      class=${editorNodeClass(
                        editor,
                        `footer.social.${index}.label`,
                        `vp-footer-social-link vp-footer-social-${socialPlatformForHref(link.href)}`,
                      )}
                      style=${editorNodeStyle(editor, `footer.social.${index}.label`)}
                      href=${link.href}
                      rel="noopener"
                      target="_blank"
                      title=${link.label}
                      @pointerdown=${(event: PointerEvent) =>
                        editorNodePointerDown(editor, `footer.social.${index}.label`, event)}
                      @click=${(event: MouseEvent) => editorNodeClick(editor, `footer.social.${index}.label`, event)}
                      >${socialIcon(link.href)}<span class="vp-visually-hidden">${link.label}</span></a
                    >
                  `,
                )}
              </nav>
            </div>
          `
        : ''}
    </div>

    <div class="vp-shell vp-footer-bottom">
      <span class="vp-footer-copyright">© ${new Date().getFullYear()} Viva Porta GmbH</span>
      <a class="vp-footer-editor-login" href="/editor">${editorLoginLabel(content.locale)}</a>
    </div>
  </footer>
`;
